import {
  Badge,
  Button,
  FormControlLabel,
  makeStyles,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Menu,
  MenuItem
} from "@material-ui/core";
import {
  AddCircleOutline,
  CheckCircle,
  HighlightOffRounded,
  HourglassEmptyRounded,
  MoveToInbox,
  Search
} from "@material-ui/icons";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import NewTicketModal from "../NewTicketModal";
import TabPanel from "../TabPanel";
import TicketsList from "../TicketsList";
import TicketsQueueSelect from "../TicketsQueueSelect";
import toastError from "../../errors/toastError";
import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.default,
  },
  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: 8,
  },
  tab: {
    minWidth: 120,
    width: 120,
  },
  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
  serachInputWrapper: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: theme.palette.primary.main,
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 25,
    padding: "10px",
    outline: "none",
  },
  badge: {
    right: 0,
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
  searchContainer: {
    display: "flex",
    padding: "10px",
    borderBottom: "2px solid rgba(0, 0, 0, .12)",
  },
  selectFilter: {
    minWidth: 120,
    margin: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.dark,
      },
    },
    "& .MuiSelect-select": {
      padding: theme.spacing(1),
    },
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [closeAction, setCloseAction] = useState(null);
  const { user } = useContext(AuthContext);
  const [, setOpenCount] = useState(0);
  const userQueueIds = user.queues.map((q) => q.id);
  const [settings, setSettings] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [previousCounts, setPreviousCounts] = useState({
    inAttendance: 0,
    waiting: 0,
    closed: 0,
  });

  const ticketsInAttendance = useTickets({
    status: "open",
    showAll: showAllTickets,
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  const ticketsWaiting = useTickets({
    status: "pending",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  const ticketsClosed = useTickets({
    status: "closed",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds: JSON.stringify(userQueueIds),
  });

  useEffect(() => {
    if (
      ticketsInAttendance.count !== previousCounts.inAttendance ||
      ticketsWaiting.count !== previousCounts.waiting ||
      ticketsClosed.count !== previousCounts.closed
    ) {
      setPreviousCounts({
        inAttendance: ticketsInAttendance.count,
        waiting: ticketsWaiting.count,
        closed: ticketsClosed.count,
      });
    }
  }, [ticketsInAttendance.count, ticketsWaiting.count, ticketsClosed.count, previousCounts]);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        console.error(err);
        toastError(err);
        setSettings([]);
      }
    };
    fetchSettings();
  }, []);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    setSearchParam(searchedTerm);
    if (searchedTerm === "") {
      setTab("open");
    } else if (tab !== "search") {
      setTab("search");
    }

  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleCloseTickets = async (status) => {
    try {
      const { data } = await api.put(`/tickets/close-all?status=${status}`);
      setAnchorEl(null);
      
      if (status === "open") {
        setTab(tab => tab === "open" ? "pending" : "open");
        setTimeout(() => setTab("open"), 100);
      } else if (status === "pending") {
        setTab(tab => tab === "pending" ? "open" : "pending");
        setTimeout(() => setTab("pending"), 100);
      } else if (status === "all") {
        setTab(tab => tab === "open" ? "pending" : "open");
        setTimeout(() => setTab("open"), 100);
      }

      setPreviousCounts({
        inAttendance: ticketsInAttendance.count,
        waiting: ticketsWaiting.count,
        closed: ticketsClosed.count,
      });

      toast.success(
        t("tickets.notifications.closed.success") + 
        (data.count ? ` (${data.count} ${t("tickets.notifications.closed.tickets")})` : "")
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error || 
        t("tickets.notifications.closed.error")
      );
    }
  };

  const handleOpenConfirmation = (status) => {
    setCloseAction(status);
    setConfirmationOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmClose = async () => {
    await handleCloseTickets(closeAction);
    setConfirmationOpen(false);
    setCloseAction(null);
  };

  const canTabsSettings = (ts) => {
    return (
      (settings && settings.some(s => s.key === ts && s.value === "enabled")) ||
      (user && user.profile === "admin")
    );
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      <ConfirmationModal
        title={
          closeAction === "all"
            ? t("ticketsManager.confirmationModal.closeAllTitle")
            : closeAction === "open"
            ? t("ticketsManager.confirmationModal.closeOpenTitle")
            : t("ticketsManager.confirmationModal.closePendingTitle")
        }
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmClose}
      >
        {closeAction === "all"
          ? t("ticketsManager.confirmationModal.closeAllMessage")
          : closeAction === "open"
          ? t("ticketsManager.confirmationModal.closeOpenMessage")
          : t("ticketsManager.confirmationModal.closePendingMessage")}
      </ConfirmationModal>
      <Paper elevation={0} square className={classes.searchContainer}>
        <Search className={classes.searchIcon} />
        <input
          type="text"
          placeholder={t("tickets.search.placeholder")}
          className={classes.searchInput}
          value={searchParam}
          onChange={handleSearch}
        />
      </Paper>
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
        >          
          <Tab
            value={"open"}
            icon={
              <Badge
                className={classes.badge}
                badgeContent={previousCounts.inAttendance}
                overlap="rectangular"
                max={9999}
                color="secondary"
              >
                <Tooltip title={t("tickets.tabs.open.title")} placement="top" arrow>
                  <MoveToInbox className={classes.tabIcon}/>
                </Tooltip>
              </Badge>
            }
            classes={{ root: classes.tab }}
          /> 
          {canTabsSettings("tabsPending") && (         
            <Tab
              value={"pending"}
              icon={
                <Badge
                  className={classes.badge}
                  badgeContent={previousCounts.waiting}
                  overlap="rectangular"
                  max={9999}
                  color="secondary"
                >
                  <Tooltip title={t("tickets.tabs.pending.title")} placement="top" arrow>
                    <HourglassEmptyRounded className={classes.tabIcon}/>
                  </Tooltip>
                </Badge>
              }
              classes={{ root: classes.tab }}
            />
          )}
          {canTabsSettings("tabsClosed") && (
            <Tab
              value={"closed"}
              icon={
                <Badge
                  className={classes.badge}
                  badgeContent={previousCounts.closed}
                  overlap="rectangular"
                  max={9999}
                  color="secondary"
                >
                  <Tooltip title={t("tickets.tabs.closed.title")} placement="top" arrow>
                    <CheckCircle className={classes.tabIcon} />
                  </Tooltip>
                </Badge>
              }  
              classes={{ root: classes.tab }}
            />
          )}
        </Tabs>  
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <Tooltip title={t("ticketsManager.buttons.newTicket")} placement="top" arrow>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setNewTicketModalOpen(true)}
          >
            <AddCircleOutline className={classes.tabIcon}/>
          </Button>
        </Tooltip>
        <Tooltip title={t("ticketsManager.buttons.closed")} placement="top" arrow>
          <Button
            variant="outlined"
            color="primary"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <HighlightOffRounded className={classes.tabIcon}/>
          </Button>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleOpenConfirmation("all")}>{t("ticketsManager.menu.all")}</MenuItem>
          <MenuItem onClick={() => handleOpenConfirmation("open")}>{t("ticketsManager.menu.open")}</MenuItem>
          <MenuItem onClick={() => handleOpenConfirmation("pending")}>{t("ticketsManager.menu.pending")}</MenuItem>
        </Menu>
        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <FormControlLabel
              label={t("tickets.buttons.showAll")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={showAllTickets}
                  onChange={() =>
                    setShowAllTickets((prevState) => !prevState)
                  }
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
          )}
        />
        <TicketsQueueSelect
          style={{ marginLeft: 6 }}
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </Paper>
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
        </Paper>
      </TabPanel>
      <TabPanel value={tab} name="pending" className={classes.ticketsWrapper}>
        <TicketsList
          status="pending"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;